import { useState } from "react"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { delegateItemOwnerByEmailAPI } from "../api/itemsApi"
import { toast } from "react-toastify"

type DelegateItemOwnerDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  collectionSlug: string | null
  itemUid: string | null
  onSuccess: () => void
}

export function DelegateItemOwnerDialog({
  open,
  onOpenChange,
  collectionSlug,
  itemUid,
  onSuccess,
}: DelegateItemOwnerDialogProps) {
  const [email, setEmail] = useState("")
  const [submitting, setSubmitting] = useState(false)

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setEmail("")
    }
    onOpenChange(next)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = email.trim()
    if (!trimmed || !trimmed.includes("@")) {
      toast.error("Enter a full email address")
      return
    }
    if (!collectionSlug || !itemUid) {
      toast.error("Missing collection or item")
      return
    }
    setSubmitting(true)
    try {
      await delegateItemOwnerByEmailAPI(collectionSlug, itemUid, trimmed)
      toast.success("Item delegated to the new owner")
      setEmail("")
      onOpenChange(false)
      onSuccess()
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Delegation failed"
      toast.error(msg)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Delegate item owner</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground py-2">
            Enter the full email address of an existing user in your organization (or an administrator).
            They will have owner-level access to this item. The original creator is unchanged.
          </p>
          <div className="grid gap-2 py-2">
            <Label htmlFor="delegate-owner-email">Email</Label>
            <Input
              id="delegate-owner-email"
              type="email"
              autoComplete="email"
              placeholder="colleague@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={submitting}
            />
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? "Delegating…" : "Delegate"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
